/**
 * ユーザーごとの「文字サイズ」設定。
 *
 * 端末のピンチズーム（拡大）を禁止しているため、その代替として
 * 各ユーザーが自分に合った拡大率を選べるようにする。
 * ルート要素（html）の font-size を % で拡大/縮小することで、
 * Tailwind の rem 基準のサイズが連動し、画面全体が
 * ブラウザのズームに近い形で一括で拡大/縮小される。
 */

export type FontScale = 'small' | 'normal' | 'large' | 'xlarge';

export interface FontScaleOption {
  key: FontScale;
  label: string;
  /** 標準を100とした拡大率(%) */
  percent: number;
  /** どんな人向けかの短い説明 */
  description: string;
}

export const FONT_SCALE_OPTIONS: FontScaleOption[] = [
  { key: 'small', label: '小', percent: 85, description: '一度に多くの情報を見たい' },
  { key: 'normal', label: '標準', percent: 100, description: '標準の文字サイズ' },
  { key: 'large', label: '大', percent: 115, description: '少し大きく見やすく' },
  { key: 'xlarge', label: '特大', percent: 130, description: '文字をはっきり大きく' },
];

export const DEFAULT_FONT_SCALE: FontScale = 'normal';

/** 値が有効な文字サイズ設定かどうか（NULL・不正値は false） */
export function isFontScale(value: unknown): value is FontScale {
  return typeof value === 'string' && FONT_SCALE_OPTIONS.some((o) => o.key === value);
}

/** 拡大率(%)を返す。未設定・不正値は標準(100)。 */
export function fontScalePercent(scale: FontScale | null | undefined): number {
  const found = FONT_SCALE_OPTIONS.find((o) => o.key === scale);
  return found ? found.percent : 100;
}

/** 表示用ラベル（例: 大 115%） */
export function fontScaleLabel(scale: FontScale | null | undefined): string {
  const found = FONT_SCALE_OPTIONS.find((o) => o.key === scale);
  return found ? `${found.label}（${found.percent}%）` : '標準（100%）';
}

/** ルート要素（html）の font-size を設定して画面全体を拡大/縮小する。 */
export function applyFontScale(scale: FontScale | null | undefined): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.fontSize = `${fontScalePercent(scale)}%`;
}